import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import React, {
  JSXElementConstructor,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { ChartRef } from "./GenerateDashboardPPTXPopup";
import OverallBarChart from "../OverallBarChart";
import { LoadsRadarChart } from "../LoadsRadarChart";
import { DeviceType } from "../../../../types/enums";
import { PercentageHistogramChart } from "../PercentageHistogramChart.js.js";
import { ActivityMLIPainSlide } from "./ActivityMLIPainSlide";
import { OverallActivityResult } from "../../../../types/overallActivityResult";
import { ActivityData } from "../../../../types/chartData";
import { Presentation, render, Shape, Slide, Text } from "react-pptx";
import { OverviewPowerpoint } from "./OverviewPowerpoint";
import { useTranslation } from "react-i18next";
import { IndustryBenchMarkRowType } from "../../../Reuseables/DashboardComponents/OverallIndustryResultsTable";
import { BenchmarkPP } from "./BenchmarkPP";

type GenerateOverviewPPTXPopupProps = {
  numOfUsers: number;
  numOfPain: number;
  numOfActivityLogs: number;
  complianceProcentage: number;
  open: boolean;
  handleClose: () => void;
  activities: OverallActivityResult[];
  industryBenchmarks: IndustryBenchMarkRowType[];
};

export const GenerateOverviewPPTXPopup: React.FC<GenerateOverviewPPTXPopupProps> = ({
  numOfUsers,
  numOfPain,
  numOfActivityLogs,
  complianceProcentage,
  open,
  handleClose,
  activities,
  industryBenchmarks,
}) => {
  const [activityCharts, setActivityCharts] = useState<ReactElement[]>([]);
  const renderedCount = useRef(0);
  const allChartsRendered = useRef(false);
  const [chartsReady, setChartsReady] = useState(false);
  const chartReferences = useRef<Map<number, ChartRef>>(new Map());
  const [activitySlides, setActivitySlides] =
    useState<ReactElement<any, string | JSXElementConstructor<any>>[]>();
  const [benchmarkSlides, setBenchmarkSlides] =
    useState<ReactElement<any, string | JSXElementConstructor<any>>[]>();
  const { t } = useTranslation();

  const downloadPowerpoint = async () => {
    try {
      const buffer = await render(
        <Presentation>
          <>
            <OverviewPowerpoint
              numOfEmployees={numOfUsers}
              numOfPainReports={numOfPain}
              compliance={complianceProcentage}
              numOfActivityLogs={numOfActivityLogs}
              activities={activities}
              translate={t}
              activitySlides={activitySlides ?? []}
              benchmarkSlides={benchmarkSlides ?? []}
            />
          </>
        </Presentation>,
        { outputType: "blob" }
      );

      const url = URL.createObjectURL(buffer as Blob);
      const link = document.createElement("a");
      link.href = url;

      var name = "presentation.pptx";

      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating presentation:", error);
    }
  };

  const handleChartRendered = () => {
    renderedCount.current += 1;
    console.log(
      `Chart rendered: ${renderedCount.current}/${chartReferences.current.size}`
    );
    if (
      renderedCount.current === chartReferences.current.size &&
      !allChartsRendered.current
    ) {
      allChartsRendered.current = true;

      chartReferences.current.forEach((x, i) => {
        x.overallBarChartB64 = x.overallBarChartRef.toBase64Image(i);
        x.distributionChartB64 = x.distributionChartRef.toBase64Image(i);
        x.distributionPainChartB64 =
          x.distributionPainChartRef.toBase64Image(i);
        x.radarChartB64 = x.radarChartRef.toBase64Image(i);
      });
      setChartsReady(true);
      // Generate activity slides
      let activityList: ReactElement[] = [];

      // making these attribues Nullable fucked up the PPTX slide. so did the little trick to bypass it.
      var activityDataNull: any;
      var datetimeNull: any;
      activities.forEach((a: OverallActivityResult, i: number) => {
        activityList.push(
          <ActivityMLIPainSlide
            key={i}
            projectName={""}
            compProjectName={"compProjectName"}
            startDate={datetimeNull}
            endDate={datetimeNull}
            compStartDate={datetimeNull}
            compEndDate={datetimeNull}
            activityData1={a.activityData}
            activityData2={activityDataNull}
            cutoffDate={a.cutoffDate}
            mliProgress={a.mliProgress}
            painProgress={a.painProgress}
            distributionChartB64={
              chartReferences.current.get(i)?.distributionChartB64 ?? ""
            }
            distributionPainChartB64={
              chartReferences.current.get(i)?.distributionPainChartB64 ?? ""
            }
            radarChartB64={chartReferences.current.get(i)?.radarChartB64 ?? ""}
          />
        );
      });

      setActivitySlides(activityList);

      const chuncked =
        industryBenchmarks.length > 4
          ? Array.from(
              { length: Math.ceil(industryBenchmarks.length / 4) },
              (_, i) => industryBenchmarks.slice(i * 4, i * 4 + 4)
            )
          : [industryBenchmarks];

      var bmList: ReactElement[] = [];

      chuncked.forEach((c, i) => {
        var slide = (
          <Slide key={i} style={{ backgroundColor: "#F9F5F1" }}>
            {/* title */}
            <Text style={{ x: 0.25, y: 0.25, w: "50%", h: 0.3, fontSize: 20 }}>
              Industry Benchmarks
            </Text>
            <Shape
              type="rect"
              style={{
                backgroundColor: "#5BBFAC",
                borderColor: "#5BBFAC",
                x: 0.25,
                y: 1,
                h: 0.02,
                w: "40%",
              }}
            />
            {c.map((element, i) => {
              // if (i !== 0) return <></>; // Just for testing;
              var x = 0;
              var y = 0;

              if (i === 0) {
                x = 0.25;
                y = 1.6;
              } else if (i === 1) {
                x = 3.25;
                y = 1.6;
              } else if (i === 2) {
                x = 0.25;
                y = 3.5;
              } else if (i === 3) {
                x = 3.25;
                y = 3.5;
              }

              return (
                <BenchmarkPP
                  key={element.id}
                  x={x}
                  y={y}
                  bm={element}
                  translation={t}
                />
              );
            })}
          </Slide>
        );

        bmList.push(slide);
      });

      setBenchmarkSlides(bmList);
    }
  };

  useEffect(() => {
    const charts: ReactElement[] = [];

    activities.forEach((a, i) => {
      chartReferences.current.set(i, {
        overallBarChartB64: "",
        overallBarChartRef: "",
        distributionChartB64: "",
        distributionChartRef: "",
        distributionPainChartB64: "",
        distributionPainChartRef: "",
        radarChartB64: "",
        radarChartRef: undefined,
      });

      charts.push(
        <Box key={i}>
          <Box height={200} width={300}>
            <OverallBarChart
              average={a.activityData.average}
              height={200}
              selection={a.activityData.activityName}
              attribute={"MLI"}
              selectAll={true}
              onRendered={handleChartRendered}
              ref={(ref) => {
                if (ref) {
                  const chartRef = chartReferences.current.get(i);
                  if (chartRef) {
                    chartRef.overallBarChartRef = ref;
                  }
                }
              }}
            />
          </Box>
          {/* {JSON.stringify(a)} */}
          <Box height={500} width={500}>
            <LoadsRadarChart
              height={500}
              selection={a.activityData.activityName ?? ""}
              deviceType={DeviceType.Back} // TODO FIX
              data1={a.activityData.deviceLoads}
              data2={null}
              pc1={"Dataset 1"}
              ref={(ref) => {
                if (ref) {
                  const chartRef = chartReferences.current.get(i);
                  if (chartRef) {
                    chartRef.radarChartRef = ref;
                  }
                }
              }}
            />
          </Box>
          <Box height={200} width={400}>
            <PercentageHistogramChart
              height={200}
              data1={a && a.activityData.percentageHistogram}
              attribute={"MLI"}
              selection={a.activityData.activityName}
              ref={(ref) => {
                if (ref) {
                  const chartRef = chartReferences.current.get(i);
                  if (chartRef) {
                    chartRef.distributionChartRef = ref;
                  }
                }
              }}
            />
          </Box>

          <Box>
            <>
              <Box height={200} width={400}>
                <PercentageHistogramChart
                  height={200}
                  data1={a && a.activityData.percentageHistogram}
                  attribute={"Pain"}
                  selection={a.activityData.activityName ?? ""}
                  ref={(ref) => {
                    if (ref) {
                      const chartRef = chartReferences.current.get(i);
                      if (chartRef) {
                        chartRef.distributionPainChartRef = ref;
                      }
                    }
                  }}
                />
              </Box>
            </>
          </Box>
        </Box>
      );
    });

    setActivityCharts(charts);
  }, []);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Generate Powerpoint</DialogTitle>
      <DialogContent>
        {chartsReady && (
          <Button onClick={downloadPowerpoint} variant="contained">
            Download
          </Button>
        )}
        {activityCharts.length > 0 &&
          activityCharts.map((c, i) => {
            return c;
          })}
      </DialogContent>
      <DialogActions>
        <Grid
          container
          sx={{ justifyContent: "space-between" }}
          direction={"row"}
        >
          <Grid item xs>
            <Button onClick={handleClose}>Close</Button>
          </Grid>
          <Grid item xs></Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
